import { IFilter } from 'services/filterRequests';
import * as Styled from './styles';
import loadingIcon from 'assets/icone/icone_loading.gif';
import { FaCheckSquare, FaRegSquare } from 'react-icons/fa';
import { useState } from 'react';
import { TextInput } from 'components/TextInput';
import { TbFilterOff, TbFilterQuestion, TbFilterSearch } from 'react-icons/tb';
import { clearText } from 'utils/clearText';
import { Button } from 'components';

interface ISimpleFilter {
  currentTab: number;
  currentLayerUuid?: string;
  loading: boolean;
  onSelect: () => void;
  onFilter: (query: string, layerUuid?: string, registrar?: boolean) => void;
  filters: IFilter[];
  appliedFilters: Record<string, string | null>;
}

interface ISelected {
  campo: string;
  campo_original: string;
  value: string[];
  layerUuid: string;
}

export function FiltroSimples({ currentLayerUuid, loading, onSelect, onFilter, filters, appliedFilters }: ISimpleFilter) {
  const [selected, setSelected] = useState<ISelected[]>([]);
  const [userFilter, setUserFilter] = useState<string>('');

  function toggleSelect(campo: string, campo_original: string, value: string, layerUuid: string) {
    setSelected(prev =>
      prev.some(s => s.campo === campo && s.layerUuid === layerUuid)
        ? prev.map(s =>
          s.campo === campo && s.layerUuid === layerUuid
            ? {
              ...s,
              value: s.value.includes(value)
                ? s.value.filter(v => v !== value)
                : [...s.value, value],
            }
            : s
        )
        : [...prev, { campo, campo_original, value: [value], layerUuid }]
    );
  }

  function getQuery(filter: IFilter, valor: string) {
    if (filter.tipo === 'number') return valor.replaceAll('.', '').replace(',', '.');
    else return `'${valor}'`
  }

  function isSelected(filter: IFilter, value: string) {
    if (!filter.layerUuid) return false;
    const item = selected.find(s => s.campo === filter.campo && s.layerUuid === filter.layerUuid);
    return !!item && item.value.includes(getQuery(filter, value));
  }

  function getFiltersByLayer() {
    const grouped = new Map<string, ISelected[]>();

    selected
      .filter(s => s.value.length > 0 && !!s.layerUuid)
      .forEach(s => {
        const key = s.layerUuid;
        const existing = grouped.get(key) ?? [];
        grouped.set(key, [...existing, s]);
      });

    const result: Record<string, string> = {};
    grouped.forEach((items, layerUuid) => {
      const query = items
        .map(s => `(${s.value.map(v => `${s.campo_original}=${v}`).join(' OR ')})`)
        .join(' AND ');
      if (query) result[layerUuid] = query;
    });

    return result;
  }

  function resetFilter() {
    setSelected([]);
    setUserFilter('');

    const layerUuidsWithAppliedFilter = Object.keys(appliedFilters).filter(layerUuid => !!appliedFilters[layerUuid]);

    if (layerUuidsWithAppliedFilter.length > 0) {
      layerUuidsWithAppliedFilter.forEach(layerUuid => onFilter('', layerUuid));
      return;
    }

    onFilter('', currentLayerUuid);
  }

  function noneSelected() {
    return selected.length === 0 || selected.every(s => s.value.length === 0);
  }

  return (
    <Styled.Wrapper>
      {loading ?
        <Styled.Loading>
          <img src={loadingIcon} alt="Loading" />
        </Styled.Loading>
        :
        <div className='content-wrapper'>
          <div className="text-input-wrapper">
            <TextInput
              icon={(<TbFilterSearch  size={20} />)}
              placeholder={'Buscar nos filtros'}
              value={userFilter}
              onChange={setUserFilter}
            />
            </div>
          <div className='filter-wrapper'>
            {filters.length === 0 ? (
              <div className="error-msg">
                <TbFilterOff className="watermark" />
                Filtro simples não disponível
              </div>
            ) :
            !filters.some(f => f.valores.some(v => clearText(v).includes(clearText(userFilter)))) ? (
              <div className="error-msg">
                <TbFilterQuestion  className="watermark" />
                Nenhum valor encontrado
              </div>
            ) :
             (
              <>
                {filters.map((filter: IFilter, index: number) => (
                  filter.valores.some(v => clearText(v).includes(clearText(userFilter))) && 
                  <div
                    key={`filtro_${index}`}
                    className='campo-wrapper'
                  >
                    <div className='titulo-campo'>
                      {filter.campo}
                    </div>
                    {
                      filter.layerTitle && 
                      <div className='subtitulo-campo'>
                        {`(${filter.layerTitle})`}
                      </div>
                    }
                    <div
                      key={`filtro_campo_${index}`}
                      className='values-wrapper'
                    >
                      {filter.valores.length === 0 ? (
                        <div className="no-values-msg">Nenhum valor disponível</div>
                      ) : (
                        filter.valores.filter(v => clearText(v).includes(clearText(userFilter))).map((value: string, index: number) => (
                          <div
                            key={`filtro_campo_${index}_value_${index}`}
                            className='value'
                          onClick={() => {
                            if (!filter.layerUuid) return;
                            const layerUuid = filter.layerUuid;
                            toggleSelect(
                              filter.campo,
                              filter.campo_original,
                              getQuery(filter, value),
                              layerUuid
                            );
                          }}
                          >
                            {isSelected(filter, value)
                              ? <FaCheckSquare title="Selecionar" className="map-icon" />
                              : <FaRegSquare title="Selecionar" className="map-icon" />
                            }
                            <span className='value-text'>{value}</span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
          {
            <div className='footer-wrapper'>
              <Button
                className="footer-button clear"
                onClick={resetFilter}
              >
                Limpar Filtro
              </Button>
              <Button
                className="footer-button"
                onClick={ () => {
                  if (noneSelected()) return resetFilter();
                  onSelect();
                  const filtersByLayer = getFiltersByLayer();
                  Object.keys(filtersByLayer).forEach(layerUuid => {
                    onFilter(filtersByLayer[layerUuid], layerUuid, true);
                  });
                }}
              >
                Executar
              </Button>
            </div>
          }
        </div>
      }
    </Styled.Wrapper>
  )
}
